import { useMemo } from 'react'
import type { JSX } from 'react'

import { formatDistance } from 'date-fns'

import {
  Flex,
  Heading,
  Sidebar,
  Skeleton,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { ListHeaderIcon } from './ListHeaderIcon'
import { RefreshButton } from './RefreshButton'

import styles from './ListHeader.module.css'

type ListHeaderTitleProps = {
  title: string
  isLoading: boolean
  isCollection: boolean
  isEmptyCollection?: boolean
  domain?: string
  lastUpdated: string | Date | null
  onRefresh: () => Promise<{ success: boolean; message: string }>
}

export const ListHeaderTitle = ({
  title,
  isCollection,
  isEmptyCollection,
  domain,
  isLoading,
  lastUpdated,
  onRefresh,
}: ListHeaderTitleProps): JSX.Element => {
  const timeAgoStr = useMemo(() => {
    if (!lastUpdated) {
      return ''
    }
    return formatDistance(new Date(lastUpdated), new Date(), {
      addSuffix: true,
    })
  }, [lastUpdated])

  const timeAgoSentence = useMemo(() => {
    if (isLoading) {
      return ''
    }
    if (isCollection && timeAgoStr) {
      return `Oldest feed refreshed ${timeAgoStr}`
    }
    if (isCollection) {
      return 'This collection has not been refreshed'
    }
    if (timeAgoStr) {
      return `Last refreshed ${timeAgoStr}`
    }
    return 'This feed has not been refreshed'
  }, [isCollection, isLoading, timeAgoStr])

  return (
    <Flex align="center" gap="sm">
      <Sidebar.Trigger className={styles.sidebarTrigger} />
      <ListHeaderIcon
        title={title}
        isCollection={isCollection}
        domain={domain}
        isLoading={isLoading}
      />
      <Flex direction="column" fullWidth gap={isLoading ? 'xs' : undefined}>
        {isLoading ? (
          <Skeleton
            width={200}
            height={32}
            className={styles.skeletonNoShrink}
          />
        ) : (
          <Heading as="h2" size="sm" truncate="ellipsis">
            {title}
          </Heading>
        )}
        {isLoading && (
          <Skeleton
            width={260}
            height={18}
            className={styles.skeletonNoShrink}
          />
        )}
        {!isLoading && !isEmptyCollection && (
          <Flex align="center" gap="2xs">
            <Text color="subtle">{timeAgoSentence}</Text>
            <RefreshButton
              refresh={async () => {
                const { success, message } = await onRefresh()
                if (!success) {
                  toast.error(message)
                } else {
                  toast.success(message)
                }
              }}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
